/**
 * Free-trial offer after the onboarding paywall (v1.5.0, docs/v150-spec.md §3.6),
 * and the day-8 second ask scheduled with it (v1.5.1, docs/v151-spec.md §2.6).
 *
 * When the onboarding paywall is closed without a purchase, PaywallPage calls
 * `scheduleTrialOffer({ babyName })`: a LOCAL notification (id 9960) 30
 * minutes later, moved past quiet hours, that opens /offer. A brand-new user
 * has no APNs device row, so a server push would reach nobody.
 *
 * Once 9960 is scheduled, the same call schedules the second ask (id 9961):
 * day 8 — 7 calendar days after the close — at 10:00 local, moved past quiet
 * hours, that opens /paywall?src=day8-notification&reason=week. Same
 * conditions, same consent, same state, its own funnel event
 * (`second_ask_scheduled`); if 9961 alone fails, the offer stands without it.
 *
 * Conditions, each with the reason returned when it fails:
 *   native iOS ................................ 'unsupported'
 *   signed in (cached Cognito sub) ............ 'signed-out'
 *   never offered to this user on this device . 'already-scheduled'
 *   prefs.offers === true (explicit consent) .. 'offers-off'
 *   notification permission granted ........... 'no-permission'
 *   a baby was created (its name) ............. 'no-baby'
 *   plan Free ................................. 'not-free'
 *   a trial the user may get .................. 'not-eligible'
 * plus 'error' when the plugin refuses the schedule.
 *
 * State lives in localStorage under `bt_trial_offer:<sub>` as
 * { scheduledAt, fireAt, dismissed, trialDays, secondAskFireAt, clearedOnPaid }
 * and is never deleted: one offer per user, ever (`secondAskFireAt` is null
 * when 9961 could not be scheduled). Cancelling (sign-out, account deletion,
 * the Settings toggle, a purchase) removes both notifications and marks the
 * state dismissed. Dismissing the dashboard notice leaves 9961 pending.
 *
 * The first time the plan is seen paid with an offer state on record —
 * dismissed or not, fired or not — useTrialOfferLifecycle cancels both
 * notifications and clears copies still in Notification Centre, then records
 * `clearedOnPaid` (ISO) so it does not repeat on every launch; a cancel that
 * timed out is retried on the next launch (docs/v151-spec.md §6.2).
 *
 * From the fire time the dashboard notice slot shows "Your N-day free trial is
 * waiting" for 14 days (useTrialOfferNotice), while the plan is Free.
 *
 * useTrialOfferLifecycle also switches Premium's nap reminders on once per account
 * (see applyNapRemindersDefault).
 */

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { silentFetch } from '../../shared/api/client';
import { getProducts } from '../../shared/services/appleIAP';
import { track } from '../../shared/services/funnel';
import { getCachedAuthUser, withTimeout } from '../../services/network';
import { isIOS, isNative } from '../../shared/utils/platform';
import { useSubscription } from '../subscription/useSubscription';
import { readJourney } from '../onboarding/onboardingStore';
import {
  TRIAL_OFFER_FALLBACK_DAYS,
  cancelSecondAskNotification,
  cancelTrialOfferNotification,
  checkPermission,
  getPrefs,
  savePrefs,
  scheduleSecondAskNotification,
  scheduleTrialOfferNotification,
  secondAskFireTime,
  trialOfferFireTime,
} from '../../services/notifications';

export const TRIAL_OFFER_KEY_PREFIX = 'bt_trial_offer:';
export const TRIAL_OFFER_NOTICE_DAYS = 14;
export const NAP_REMINDERS_DEFAULT_KEY_PREFIX = 'bt_nap_reminders_default:';

const DAY_MS = 24 * 60 * 60 * 1000;
const CANCEL_TIMEOUT_MS = 2000;
const MAX_TIMEOUT_MS = 2 ** 31 - 1; // setTimeout's ceiling (~24.8 days)

// --- State (localStorage, per Cognito sub) ---------------------------------

function currentSub() {
  return getCachedAuthUser()?.sub || null;
}

function readRaw(sub) {
  if (!sub) return null;
  try {
    return localStorage.getItem(`${TRIAL_OFFER_KEY_PREFIX}${sub}`);
  } catch {
    return null;
  }
}

function parseState(raw) {
  if (!raw) return null;
  try {
    const state = JSON.parse(raw);
    return state && typeof state === 'object' ? state : null;
  } catch {
    return null;
  }
}

const listeners = new Set();

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function writeState(sub, state) {
  if (!sub) return;
  try {
    localStorage.setItem(`${TRIAL_OFFER_KEY_PREFIX}${sub}`, JSON.stringify(state));
  } catch {
    /* storage unavailable — the notice simply won't show */
  }
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* one bad listener must not break the rest */
    }
  });
}

/** The current user's offer state, or null when none was ever scheduled. */
export function readTrialOfferState(sub = currentSub()) {
  return parseState(readRaw(sub));
}

const toMs = (value) => (typeof value === 'number' ? value : Date.parse(value || ''));

// --- Trial length -----------------------------------------------------------

/**
 * The yearly plan's free-trial length as StoreKit reports it right now:
 * a number of days; null when StoreKit lists the plan without a free trial;
 * undefined when StoreKit has not answered (or this build does not report a
 * trial length) — callers then quote the static 7-day fallback.
 */
export function liveTrialDays() {
  try {
    const yearly = (getProducts() || []).find(
      (p) => p && (p.billingPeriod === 'P1Y' || /yearly/i.test(String(p.id || '')))
    );
    if (!yearly || !yearly.price || !('trialDays' in yearly)) return undefined;
    const days = Number(yearly.trialDays);
    return Number.isInteger(days) && days > 0 ? days : null;
  } catch {
    return undefined;
  }
}

// --- Scheduling -------------------------------------------------------------

async function runSchedule({ babyName, plan, trialEligible } = {}) {
  if (!isNative() || !isIOS()) return { scheduled: false, reason: 'unsupported' };
  const sub = currentSub();
  if (!sub) return { scheduled: false, reason: 'signed-out' };
  if (readRaw(sub)) return { scheduled: false, reason: 'already-scheduled' };
  if (getPrefs().offers !== true) return { scheduled: false, reason: 'offers-off' };
  if (!(await checkPermission())) return { scheduled: false, reason: 'no-permission' };
  const name = String(babyName || '').trim();
  if (!name) return { scheduled: false, reason: 'no-baby' };

  // The paywall has just shown a Free account, but ask the server rather than
  // trust a cache. A caller may pass the plan it already holds. When the
  // status cannot be fetched the offer is still scheduled — the paywall was
  // only shown to a Free account, and useTrialOfferLifecycle cancels it if
  // the plan turns out to be paid.
  let status = { plan, trialEligible };
  if (plan === undefined) status = (await silentFetch('/subscription/status', 'GET')) || {};
  if (status.plan && status.plan !== 'free') return { scheduled: false, reason: 'not-free' };
  // Trial wording only for an account that may get the trial: the server says
  // false when it has Apple purchase history, StoreKit null when the yearly
  // plan carries no free trial at all.
  const live = liveTrialDays();
  if (status.trialEligible === false || live === null) return { scheduled: false, reason: 'not-eligible' };
  const trialDays = Number.isInteger(live) ? live : TRIAL_OFFER_FALLBACK_DAYS;

  const fireAt = trialOfferFireTime(new Date(), getPrefs());
  const result = await scheduleTrialOfferNotification({ babyName: name, trialDays, fireAt });
  if (!result.scheduled) return result;

  // The day-8 second ask rides on the offer: same conditions, same state.
  const secondAskAt = secondAskFireTime(new Date(), getPrefs());
  let second = { scheduled: false };
  try {
    second = await scheduleSecondAskNotification({ babyName: name, trialDays, fireAt: secondAskAt });
  } catch {
    /* ignored — the offer stands without it */
  }

  writeState(sub, {
    scheduledAt: new Date().toISOString(),
    fireAt: fireAt.toISOString(),
    dismissed: false,
    trialDays,
    secondAskFireAt: second.scheduled ? secondAskAt.toISOString() : null,
  });
  track('offer_scheduled');
  if (second.scheduled) track('second_ask_scheduled');
  return { scheduled: true, fireAt: fireAt.toISOString() };
}

let scheduling = null; // the in-flight call, shared — a double close schedules once

/**
 * Schedule the one-off trial offer, and the day-8 second ask with it. Resolves
 * { scheduled: true, fireAt } (the offer's) or { scheduled: false, reason }
 * (reasons in the header). Never rejects.
 * Optional `plan` / `trialEligible` (from useSubscription) skip the status
 * fetch.
 */
export function scheduleTrialOffer(options = {}) {
  if (!scheduling) {
    scheduling = runSchedule(options)
      .catch(() => ({ scheduled: false, reason: 'error' }))
      .finally(() => {
        scheduling = null;
      });
  }
  return scheduling;
}

/**
 * Cancel the offer: remove the pending (or delivered) notifications — the
 * 9960 offer and the 9961 second ask — and mark this user's offer dismissed so
 * the dashboard notice never shows. Called by sign-out and account deletion
 * (BEFORE Auth.signOut, while the user is still known), the Settings offers
 * toggle and useTrialOfferLifecycle. Never rejects and never hangs a sign-out:
 * resolves true once the plugin has answered both cancels (on the web there is
 * nothing to cancel), false when it did not answer within CANCEL_TIMEOUT_MS.
 */
export async function cancelTrialOffer() {
  try {
    const sub = currentSub();
    const state = readTrialOfferState(sub);
    if (sub && state && !state.dismissed) writeState(sub, { ...state, dismissed: true });
    await withTimeout(
      Promise.all([cancelTrialOfferNotification(), cancelSecondAskNotification()]),
      CANCEL_TIMEOUT_MS
    );
    return true;
  } catch {
    /* best effort */
    return false;
  }
}

// --- Hooks --------------------------------------------------------------------

/** Re-renders at `startMs` and again at `endMs`, so the notice appears and expires on time. */
function useWindowClock(startMs, endMs) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!Number.isFinite(startMs)) return undefined;
    const current = Date.now();
    let next = null;
    if (current < startMs) next = startMs;
    else if (current < endMs) next = endMs;
    if (next === null) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(next - current + 500, MAX_TIMEOUT_MS));
    return () => clearTimeout(timer);
  }, [startMs, endMs, now]);
  return now;
}

/**
 * The dashboard notice: visible from the fire time until 14 days later, while
 * the plan is Free, the offer has not been dismissed and a trial is on offer.
 * Returns { visible, dismiss, trialDays }.
 */
export function useTrialOfferNotice() {
  const [sub] = useState(currentSub);
  const raw = useSyncExternalStore(
    subscribe,
    () => readRaw(sub),
    () => null
  );
  const state = useMemo(() => parseState(raw), [raw]);
  const { plan, isLoading, trialEligible, hasStatus } = useSubscription();

  const fireAtMs = toMs(state?.fireAt);
  const endMs = fireAtMs + TRIAL_OFFER_NOTICE_DAYS * DAY_MS;
  const now = useWindowClock(fireAtMs, endMs);

  const live = liveTrialDays();
  const stored = Number(state?.trialDays);
  const trialDays = Number.isInteger(live)
    ? live
    : Number.isInteger(stored) && stored > 0
      ? stored
      : TRIAL_OFFER_FALLBACK_DAYS;

  const visible =
    !!state &&
    !state.dismissed &&
    Number.isFinite(fireAtMs) &&
    now >= fireAtMs &&
    now < endMs &&
    !isLoading &&
    // Fails closed (docs/v151-spec.md §6.2): no trial notice until the server
    // has answered — a failed or offline-paused status is not "eligible".
    hasStatus === true &&
    plan === 'free' &&
    trialEligible !== false &&
    live !== null;

  const dismiss = useCallback(() => {
    const current = readTrialOfferState(sub);
    if (sub && current && !current.dismissed) writeState(sub, { ...current, dismissed: true });
  }, [sub]);

  return { visible, dismiss, trialDays };
}

// --- Premium nap-reminder default ---------------------------------------------

const napDefaultInMemory = new Set(); // storage refused: decided for this session
let napDefaultRunning = false; // StrictMode runs the effect twice — decide once

function napDefaultDecided(sub) {
  if (napDefaultInMemory.has(sub)) return true;
  try {
    return !!localStorage.getItem(`${NAP_REMINDERS_DEFAULT_KEY_PREFIX}${sub}`);
  } catch {
    return false;
  }
}

function markNapDefaultDecided(sub) {
  try {
    localStorage.setItem(`${NAP_REMINDERS_DEFAULT_KEY_PREFIX}${sub}`, new Date().toISOString());
  } catch {
    napDefaultInMemory.add(sub);
  }
}

/**
 * Nap and bedtime reminders — and with them the lock-screen Live Activity — are a
 * Premium setting that stays off until switched on (prefs.napReminders). Up to 1.4.3 a
 * new account was inside its 7-day preview when the post-baby prompt ran, and the prompt
 * switched them on. A 1.5.0 account is on Free at that point, so the onboarding's
 * "Allow notifications" leaves them off, and nothing on the way to Premium (a purchase,
 * the yearly trial, a pass) switched them on.
 *
 * So, once per account on this device: the first time an account whose first-run
 * journey happened here (onboardingStore.readJourney) is seen on a paid plan, nap
 * reminders are switched on — only when notification permission is already granted
 * (checked silently, never prompted). The decision is recorded either way
 * ('bt_nap_reminders_default:<sub>'), so a later "off" in Settings is respected, and an
 * account that predates the journey (an existing subscriber updating) keeps its
 * settings exactly as they are. Never rejects.
 */
export async function applyNapRemindersDefault(sub = currentSub()) {
  if (!sub || napDefaultRunning || napDefaultDecided(sub) || !readJourney(sub)) return;
  napDefaultRunning = true;
  try {
    const granted = await checkPermission();
    if (granted) {
      const prefs = getPrefs();
      if (prefs.napReminders !== true) savePrefs({ ...prefs, napReminders: true });
    }
    markNapDefaultDecided(sub);
  } catch {
    /* best effort — the Settings toggle still works */
  } finally {
    napDefaultRunning = false;
  }
}

/**
 * Mounted once in Layout: when the plan turns paid (a purchase, a restore, a
 * comp or a pass) the offer is cleared — a new subscriber must never be told
 * their free trial is waiting, nor asked again on day 8 — and Premium's nap
 * reminders get their once-per-account default (applyNapRemindersDefault).
 *
 * Clearing (docs/v151-spec.md §6.2): whenever an offer state exists, dismissed
 * or not, fire times past or not, cancelTrialOffer() cancels both
 * notifications and removes their delivered copies, then `clearedOnPaid` is
 * recorded so later launches leave it alone. A cancel that timed out records
 * nothing, so the next launch tries again.
 */
export function useTrialOfferLifecycle() {
  const { plan, isLoading } = useSubscription();
  const paid = !isLoading && !!plan && plan !== 'free';

  useEffect(() => {
    if (!paid) return;
    const sub = currentSub();
    const state = readTrialOfferState(sub);
    if (!state || state.clearedOnPaid) return;
    cancelTrialOffer().then((cleared) => {
      // Re-read: cancelTrialOffer has just marked the state dismissed.
      const current = readTrialOfferState(sub);
      if (cleared && current) writeState(sub, { ...current, clearedOnPaid: new Date().toISOString() });
    });
  }, [paid]);

  useEffect(() => {
    if (paid) applyNapRemindersDefault();
  }, [paid]);
}
